// ADR-0003 D2 / D4 / D10: the local store. Every synced table carries id,
// updatedAt, deletedAt and owner. The outbox (syncQueue) and the
// incremental-pull cursors (syncMeta) live in the same Dexie instance.
//
// F-1 (T-030): one database per user. The active db is `calicool-<userId>`
// while a user is signed in, and an anonymous `calicool` instance is
// used by tests and (transiently) by the auth flow's restore phase.
// `getDb()` returns the active instance or throws. The auth flow owns
// the lifecycle: `openDbFor(userId)` on sign-in, `deleteActiveDb()` on
// sign-out. The sync engine reads the active db through this same API
// so the swap is transparent.

import Dexie, { type Table } from "dexie";
import { newId } from "./ids";

/** Local row shape shared by every synced table. */
export interface SyncedRow {
  id: string;
  owner: string;
  updatedAt: string;
  deletedAt: string | null;
  [field: string]: unknown;
}

export type SyncOp = "create" | "update" | "delete";

export interface SyncQueueEntry {
  /** Queue-internal id (not the record id). */
  id: string;
  collection: string;
  recordId: string;
  op: SyncOp;
  /** JSON-encoded payload — full row for create/update; `{}` for delete.
   *  ADR-0011 D1: for `create` of a `progress_photos` row the bytes
   *  are NOT here — see `blobKey`. The scalar fields stay in the
   *  payload so coalescing / LWW / superseded logic is unchanged. */
  payload: string;
  /** ADR-0011 D1: present only on `progress_photos` create entries
   *  that carry a file. Equals the `recordId` (one blob per record). */
  blobKey?: string;
  /** ADR-0011 D2: the server field name. Currently always "photo". */
  fileField?: string;
  attempts: number;
  /** T-097 §4 (ADR-0003 rev 2 D4 + D7): `held` means *written locally
   *  on purpose and deliberately not pushed yet*. Only `startWorkout`
   *  uses it — for the `workout_logs` create — and `logSet` flips
   *  the held entry to `pending` in the same Dexie transaction as
   *  the first `log_entries` create. A held entry is dropped when
   *  its row is discarded (`softDeleteWorkout`, sign-out); the push
   *  selector ignores it; the coalescer must treat a held create as
   *  a barrier (no pending op for that recordId goes ahead of it). */
  status: "pending" | "failed" | "held";
  createdAt: string;
  lastError?: string;
}

export interface SyncMetaRow {
  collection: string;
  cursor: string;
}

export interface WorkoutLogRow extends SyncedRow {
  started_at: string;
  completed_at?: string | null;
}

export interface LogEntryRow extends SyncedRow {
  workout: string;
  exercise: string;
  set_number: number;
  reps?: number;
  hold_seconds?: number;
}

export interface UserProgressRow extends SyncedRow {
  exercise: string;
  /** T-090 R-25 H1: the live server's required select on `user_progress`.
   *  One of `locked` / `beginner` / `intermediate` / `mastery`. The
   *  local equivalent on `ExerciseProgress` is `bestTier`; we map
   *  `none` → `locked` here so a fresh row is in the server's enum. */
  current_status?: "locked" | "beginner" | "intermediate" | "mastery";
  /** The moment the user crossed into the current `current_status`,
   *  ISO-8601. The runner writes `now()` whenever the tier rises. */
  unlocked_at?: string;
  /** Local-only: how many completed workouts contributed to this
   *  row. The server does not see this field; the sync manager
   *  drops it (or the server ignores it on POST). */
  completedWorkouts?: number;
}

export interface ProgressPhotoRow extends SyncedRow {
  taken_at: string;
  file?: string;
}

export interface CommunityPostRow extends SyncedRow {
  content: string;
  squad_coach?: string | null;
  workout_ref?: string | null;
}

export interface PostCommentRow extends SyncedRow {
  post: string;
  content: string;
}

export interface PostLikeRow extends SyncedRow {
  post: string;
}

export interface SocialFollowRow extends SyncedRow {
  target: string;
}

export interface CoachAthleteRow extends SyncedRow {
  coach: string;
  athlete: string;
  status: string;
}

export interface CoachNoteRow extends SyncedRow {
  coach: string;
  athlete: string;
  text: string;
}

/** ADR-0011 D1 — a photo queued for upload. The row carries the
 *  record id (one blob per row, no separate key), the compressed
 *  Blob, and the `uploaded` flag the engine flips on success. The
 *  `width` / `height` are stored so the gallery can size placeholders
 *  before the file is fetched; `lastUsedAt` powers the LRU display
 *  trim (ADR-0007 D6).
 *
 *  IMPORTANT (ADR-0011 errata E-4): the primary key is `id`, not
 *  `recordId`. Dexie 4 cannot change a primary key across a version
 *  upgrade (`node_modules/dexie/dist/dexie.js:3832` throws
 *  `UpgradeError: Not yet support for changing primary key`), and
 *  every current user's per-user db is at the v4 schema (which
 *  declared `photoBlobs: "id"`). The semantic is unchanged: the row's
 *  `id` IS the record id of the `progress_photos` row, and it is the
 *  same value as `blobKey` on the corresponding sync queue entry. */
export interface PhotoBlobRow {
  /** Same id as the `progress_photos` row. */
  id: string;
  blob: Blob;
  mime: string;
  sizeBytes: number;
  width: number;
  height: number;
  createdAt: string;
  lastUsedAt: string;
  /** True once the server has the file (engine writes this in the same
   *  transaction that removes the outbox entry). */
  uploaded: boolean;
}

/** ADR-0011 errata E-4: there is no module-global Blob cache. The
 *  Dexie row IS the source of truth for the bytes — a previous
 *  design kept a separate `Map<string, Blob>` because jsdom's
 *  `structuredClone` does not preserve Blob instances, but the real
 *  browser's IndexedDB does (and so does Node 24 + fake-indexeddb
 *  6.2.5, the test environment for this file). The photoSync tests
 *  run in the node environment for exactly this reason; under jsdom
 *  the workaround hid two production defects (survival across
 *  sign-out, and an unbounded LRU trim) while breaking the
 *  production code path that test 8 was meant to exercise. */

class CaliCoolDb extends Dexie {
  workouts!: Table<SyncedRow, string>;
  workout_logs!: Table<WorkoutLogRow, string>;
  log_entries!: Table<LogEntryRow, string>;
  user_progress!: Table<UserProgressRow, string>;
  progress_photos!: Table<ProgressPhotoRow, string>;
  community_posts!: Table<CommunityPostRow, string>;
  post_comments!: Table<PostCommentRow, string>;
  post_likes!: Table<PostLikeRow, string>;
  social_follows!: Table<SocialFollowRow, string>;
  coach_athletes!: Table<CoachAthleteRow, string>;
  coach_notes!: Table<CoachNoteRow, string>;
  photoBlobs!: Table<PhotoBlobRow, string>;
  syncQueue!: Table<SyncQueueEntry, string>;
  syncMeta!: Table<SyncMetaRow, string>;

  constructor(name: string) {
    super(name);
    // ADR-0003 D10: never edit a published version. Bump by adding a new
    // .version(n) block.
    this.version(1).stores({
      workouts: "id, updatedAt, deletedAt, owner",
      syncQueue: "id, collection, recordId, status, createdAt, [collection+status]",
      syncMeta: "collection",
    });
    this.version(2)
      .stores({
        workouts: "id, updatedAt, deletedAt, owner", // v1 kept for migration tests
        workout_logs: "id, updatedAt, deletedAt, owner, started_at, completed_at",
        log_entries: "id, updatedAt, deletedAt, owner, workout, exercise, setNumber",
        syncQueue: "id, collection, recordId, status, createdAt, [collection+status]",
        syncMeta: "collection",
      })
      .upgrade(async (tx) => {
        // Keep the v1 outbox rows untouched; they still reference the v1
        // `workouts` table. The sync manager falls back to `workouts` when
        // a queued record is not found in the new `workout_logs` table.
        void tx;
      });
    this.version(3)
      .stores({
        workouts: "id, updatedAt, deletedAt, owner",
        workout_logs: "id, updatedAt, deletedAt, owner, started_at, completed_at",
        log_entries: "id, updatedAt, deletedAt, owner, workout, exercise, setNumber",
        user_progress: "id, updatedAt, deletedAt, owner, exercise",
        progress_photos: "id, updatedAt, deletedAt, owner, taken_at",
        community_posts: "id, updatedAt, deletedAt, owner",
        post_comments: "id, updatedAt, deletedAt, owner, post",
        post_likes: "id, updatedAt, deletedAt, owner, post",
        social_follows: "id, updatedAt, deletedAt, owner, target",
        coach_athletes: "id, updatedAt, deletedAt, owner, coach, athlete",
        coach_notes: "id, updatedAt, deletedAt, owner, coach, athlete",
        photoBlobs: "id",
        syncQueue: "id, collection, recordId, status, createdAt, [collection+status]",
        syncMeta: "collection",
      })
      .upgrade(async (tx) => {
        void tx;
      });
    this.version(4)
      .stores({
        workouts: "id, updatedAt, deletedAt, owner",
        workout_logs: "id, updatedAt, deletedAt, owner, started_at, completed_at",
        log_entries: "id, updatedAt, deletedAt, owner, workout, exercise, set_number",
        user_progress: "id, updatedAt, deletedAt, owner, exercise",
        progress_photos: "id, updatedAt, deletedAt, owner, taken_at",
        community_posts: "id, updatedAt, deletedAt, owner",
        post_comments: "id, updatedAt, deletedAt, owner, post",
        post_likes: "id, updatedAt, deletedAt, owner, post",
        social_follows: "id, updatedAt, deletedAt, owner, target",
        coach_athletes: "id, updatedAt, deletedAt, owner, coach, athlete",
        coach_notes: "id, updatedAt, deletedAt, owner, coach, athlete",
        photoBlobs: "id",
        syncQueue: "id, collection, recordId, status, createdAt, [collection+status]",
        syncMeta: "collection",
      })
      .upgrade(async (tx) => {
        // Gate 0 D1: local log_entries rows now use the server field names
        // set_number and hold_seconds verbatim (ADR-0008 D5). v2/v3 used
        // camelCase before the rule was explicit. Existing rows are left
        // untouched — this is a new published version, not an in-place edit
        // (ADR-0003 D10).
        void tx;
      });
    this.version(5)
      .stores({
        workouts: "id, updatedAt, deletedAt, owner",
        workout_logs: "id, updatedAt, deletedAt, owner, started_at, completed_at",
        log_entries: "id, updatedAt, deletedAt, owner, workout, exercise, set_number",
        user_progress: "id, updatedAt, deletedAt, owner, exercise",
        progress_photos: "id, updatedAt, deletedAt, owner, taken_at",
        community_posts: "id, updatedAt, deletedAt, owner",
        post_comments: "id, updatedAt, deletedAt, owner, post",
        post_likes: "id, updatedAt, deletedAt, owner, post",
        social_follows: "id, updatedAt, deletedAt, owner, target",
        coach_athletes: "id, updatedAt, deletedAt, owner, coach, athlete",
        coach_notes: "id, updatedAt, deletedAt, owner, coach, athlete",
        // ADR-0011 D1 / errata E-4: photoBlobs is keyed by `id` (same
        // value as the progress_photos row id, same value as the
        // syncQueue.blobKey). The primary key MUST stay `id` —
        // changing it across a Dexie version throws at open time
        // (`dexie.js:3832`). Indexed by sizeBytes (queueFullBytes),
        // createdAt and lastUsedAt (LRU trim).
        photoBlobs: "id, sizeBytes, createdAt, lastUsedAt",
        syncQueue: "id, collection, recordId, status, createdAt, [collection+status]",
        syncMeta: "collection",
      });
    // No `.upgrade()` is needed for v4 → v5: nothing was ever written
    // to v4's photoBlobs in production (the v4 declaration was a
    // placeholder; R-16 ships the real writers). Adding any upgrade
    // step here would be unreachable code.
  }
}

let _activeDb: CaliCoolDb | null = null;
let _activeUserId: string | null = null;

/**
 * The currently active database. Throws if no user is signed in and no
 * anonymous db has been opened. The auth flow opens a per-user db on
 * sign-in and deletes it on sign-out; tests use `openAnonymousDb()`.
 */
export function getDb(): CaliCoolDb {
  if (!_activeDb) {
    throw new Error(
      "db: no active database — call openDbFor(userId) or openAnonymousDb() first",
    );
  }
  return _activeDb;
}

/** The id of the user whose db is active, or `null` for the anonymous db. */
export function getActiveUserId(): string | null {
  return _activeUserId;
}

/** Open the per-user database for `userId`. Closes any active db first. */
// Pub/sub for the active-database lifecycle. Routes that read Dexie on
// mount subscribe through `hooks/useActiveUserId` and render their
// loading state until a database is active, instead of racing the auth
// effect that opens it. On a cold load of /app/progress the route's own
// effect ran BEFORE the AuthProvider's user effect (children first), hit
// `getDb()` with no active database, and rendered the error boundary —
// present since T-053 (2026-08-22), found by the human's walk on
// 2026-09-06.
type ActiveDbListener = () => void;
const activeDbListeners: Set<ActiveDbListener> = new Set();

export function onActiveDbChange(listener: ActiveDbListener): () => void {
  activeDbListeners.add(listener);
  return () => activeDbListeners.delete(listener);
}

function fireActiveDbChange(): void {
  for (const l of activeDbListeners) l();
}

export function openDbFor(userId: string): CaliCoolDb {
  if (_activeDb && _activeDb.isOpen()) _activeDb.close();
  _activeDb = new CaliCoolDb(`calicool-${userId}`);
  _activeUserId = userId;
  fireActiveDbChange();
  return _activeDb;
}

/** Close the active handle and keep the data on disk — the state a cold
 *  page load starts from (no handle, data present). The next
 *  `openDbFor` reopens the same database. */
export async function closeActiveDb(): Promise<void> {
  if (!_activeDb) return;
  try {
    _activeDb.close();
  } catch {
    /* ignore — db may already be closed */
  }
  _activeDb = null;
  _activeUserId = null;
  fireActiveDbChange();
}

/**
 * Open the anonymous database (landing page, tests that do not sign in).
 * Always creates a fresh Dexie instance so the Proxy's cached
 * `Reflect.get` does not hold a reference to a closed/deleted instance.
 */
export function openAnonymousDb(): CaliCoolDb {
  if (_activeDb && _activeDb.isOpen()) _activeDb.close();
  _activeDb = new CaliCoolDb("calicool");
  _activeUserId = null;
  fireActiveDbChange();
  return _activeDb;
}

/**
 * Backward-compatibility alias. Every method is a thunk that reads
 * the active db at call time. Prefer `getDb()` in new code. Kept for
 * the existing test files that import `db` as a binding; avoids the
 * `Proxy` approach which mishandles Dexie's internal state after
 * `delete()`.
 */
export const db = {
  get user_progress() {
    return getDb().user_progress;
  },
  get progress_photos() {
    return getDb().progress_photos;
  },
  get community_posts() {
    return getDb().community_posts;
  },
  get post_comments() {
    return getDb().post_comments;
  },
  get post_likes() {
    return getDb().post_likes;
  },
  get social_follows() {
    return getDb().social_follows;
  },
  get coach_athletes() {
    return getDb().coach_athletes;
  },
  get coach_notes() {
    return getDb().coach_notes;
  },
  get photoBlobs() {
    return getDb().photoBlobs;
  },
  get workouts() {
    return getDb().workouts;
  },
  get workout_logs() {
    return getDb().workout_logs;
  },
  get log_entries() {
    return getDb().log_entries;
  },
  get syncQueue() {
    return getDb().syncQueue;
  },
  get syncMeta() {
    return getDb().syncMeta;
  },
  table(name: string): Table {
    return getDb().table(name);
  },
  get tables(): Table[] {
    return getDb().tables;
  },
  delete(): Promise<void> {
    const active = getDb();
    const name = active.name;
    try {
      active.close();
    } catch {
      /* ignore */
    }
    _activeDb = null;
    _activeUserId = null;
    fireActiveDbChange();
    // Use Dexie's static delete: instance delete leaves Dexie's
    // internal state corrupted, which breaks subsequent transactions
    // in the same test file.
    return Dexie.delete(name);
  },
  open(): Promise<unknown> {
    if (!_activeDb) {
      _activeDb = new CaliCoolDb("calicool");
      _activeUserId = null;
      fireActiveDbChange();
    }
    return _activeDb.open();
  },
  close(): void {
    getDb().close();
  },
  transaction(
    mode: "r" | "rw",
    tables: unknown,
    fn: (...args: unknown[]) => unknown,
  ): Promise<unknown> {
    return (getDb() as unknown as {
      transaction: (...a: unknown[]) => Promise<unknown>;
    }).transaction(mode, tables, fn);
  },
} as unknown as CaliCoolDb;

/** Delete the active db. F-1: called on sign-out to wipe user data. */
export async function deleteActiveDb(): Promise<void> {
  if (!_activeDb) return;
  const name = _activeDb.name;
  try {
    _activeDb.close();
  } catch {
    /* ignore — db may already be closed */
  }
  // Use Dexie's static delete (not the instance's, which can fail if
  // the connection is in a weird state).
  await Dexie.delete(name);
  _activeDb = null;
  _activeUserId = null;
  fireActiveDbChange();
}

/**
 * Owner of locally-created rows. Updated by the auth flow: `setOwner(user.id)`
 * on every path where user becomes non-null, `setOwner(null)` on sign-out.
 * A `null` owner means the data has no place to go and the server rule
 * would reject the push, so we refuse the write (DEF-3 guard).
 */
let currentOwner: string | null = null;

export function setOwner(owner: string | null): void {
  currentOwner = owner;
}

export function getOwner(): string | null {
  return currentOwner;
}

function newQueueId(): string {
  return newId();
}

function nowIso(): string {
  return new Date().toISOString();
}

function queueEntry(
  collection: string,
  recordId: string,
  op: SyncOp,
  row: SyncedRow | null,
  status: "pending" | "held" = "pending",
): SyncQueueEntry {
  return {
    id: newQueueId(),
    collection,
    recordId,
    op,
    payload: row ? JSON.stringify(stripInternalFields(row)) : "{}",
    attempts: 0,
    status,
    createdAt: nowIso(),
  };
}

/** Strip the fields the server manages itself (id, owner, updatedAt,
 *  deletedAt) plus the file fields that live in `photoBlobs` only —
 *  ADR-0011 D2 says the `photo` field never rides a `create` (it is
 *  uploaded via multipart) and never rides an `update` (an LWW update
 *  cannot clear the file). The local row still stores the value so
 *  the UI knows what filename the server gave us; it just never goes
 *  back in the JSON payload. */
function stripInternalFields(row: SyncedRow): Record<string, unknown> {
  const { id, owner, updatedAt, deletedAt, photo, ...rest } = row as SyncedRow & {
    photo?: unknown;
  };
  void id;
  void owner;
  void photo;
  return { id: row.id, owner: row.owner, updatedAt, deletedAt, ...rest };
}

export interface CreateWorkoutInput {
  name: string;
  notes?: string;
  [field: string]: unknown;
}

/** ADR-0003 D4: row + queue entry commit in one Dexie transaction.
 *  B12: legacy `createWorkout` now writes to the `workout_logs` table so
 *  existing tests and the sync manager share the same local store.
 */
export async function createWorkout(input: CreateWorkoutInput): Promise<string> {
  if (currentOwner == null) {
    throw new Error("db: owner not set — call setOwner(userId) after sign-in");
  }
  const owner = currentOwner;
  const id = newId();
  const db = getDb();
  const out = await db.transaction("rw", db.workout_logs, db.syncQueue, async () => {
    const row: SyncedRow = {
      ...input,
      id,
      owner,
      updatedAt: nowIso(),
      deletedAt: null,
    };
    await db.workout_logs.put(row as WorkoutLogRow);
    await db.syncQueue.add(queueEntry("workout_logs", id, "create", row));
    return id;
  });
  fireLocalWrite();
  return out;
}

export async function updateWorkout(
  id: string,
  patch: Partial<CreateWorkoutInput>,
): Promise<void> {
  if (currentOwner == null) {
    throw new Error("db: owner not set — call setOwner(userId) after sign-in");
  }
  const db = getDb();
  await db.transaction("rw", db.workout_logs, db.syncQueue, async () => {
    const existing = await db.workout_logs.get(id);
    if (!existing) throw new Error(`workout ${id} not found`);
    const next: SyncedRow = {
      ...existing,
      ...patch,
      id,
      owner: existing.owner,
      updatedAt: nowIso(),
      deletedAt: existing.deletedAt,
    };
    await db.workout_logs.put(next as WorkoutLogRow);
    await db.syncQueue.add(queueEntry("workout_logs", id, "update", next));
  });
  fireLocalWrite();
}

export async function softDeleteWorkout(id: string): Promise<void> {
  // No-op if there is no active database (a test closed it in
  // beforeEach, or we are in teardown). Without this guard the
  // throw from `getDb()` becomes an unhandled rejection when the
  // call is on a cleanup microtask that ran after the db was closed.
  if (!_activeDb) return;
  if (_activeDb.isOpen() === false) return;
  const ts = nowIso(); // MIN-3: single timestamp
  const db = getDb();
  const existing = await db.workout_logs.get(id);
  await db.transaction("rw", db.workout_logs, db.syncQueue, async () => {
    const ex = existing ?? (await db.workout_logs.get(id));
    if (!ex) throw new Error(`workout ${id} not found`);
    // T-097 §4: if the workout_logs create is still held (no set
    // ever landed), the server never saw the row. Tombstone the
    // local row, delete the held entry, and enqueue NOTHING — the
    // delete would be a 404 and the superseded probe would
    // permanent-fail the entry.
    const held = await db.syncQueue
      .where("[collection+status]")
      .equals(["workout_logs", "held"])
      .filter((e) => e.recordId === id)
      .first();
    if (held) {
      await db.syncQueue.delete(held.id);
      const tombstone: SyncedRow = {
        ...ex,
        id,
        owner: ex.owner,
        updatedAt: ts,
        deletedAt: ts,
      };
      await db.workout_logs.put(tombstone as WorkoutLogRow);
      return;
    }
    // Pending or pushed: enqueue a delete (or coalesce to cancel
    // with a held entry, which there is not).
    const next: SyncedRow = {
      ...ex,
      id,
      owner: ex.owner,
      updatedAt: ts,
      deletedAt: ts,
    };
    await db.workout_logs.put(next as WorkoutLogRow);
    await db.syncQueue.add(queueEntry("workout_logs", id, "delete", null));
  });
  // T-090 R-25 H2: clear the start registry so a later sweep
  // doesn't look at this id. The owner is read from the existing
  // row we already loaded above (the read happened once, before
  // the transaction, so this is a single round-trip in practice).
  if (existing) clearStartedHere(existing.owner, id);
  fireLocalWrite();
}

/** B12: create a single workout_logs row when the runner starts a session. */
export async function startWorkout(): Promise<string> {
  if (currentOwner == null) {
    throw new Error("db: owner not set — call setOwner(userId) after sign-in");
  }
  const owner = currentOwner;
  const id = newId();
  const db = getDb();
  const out = await db.transaction("rw", db.workout_logs, db.syncQueue, async () => {
    const row: WorkoutLogRow = {
      id,
      owner,
      updatedAt: nowIso(),
      deletedAt: null,
      started_at: nowIso(),
      completed_at: null,
    };
    await db.workout_logs.put(row as WorkoutLogRow);
    // T-097 §4: the create is HELD — it does not go to the server
    // until `logSet` flips it to pending in the same transaction as
    // the first log_entries create. A zero-set start then never
    // exists anywhere but this device, and discarding it drops the
    // held entry with the row — no server-side row to to delete.
    await db.syncQueue.add(queueEntry("workout_logs", id, "create", row, "held"));
    return id;
  });
  // T-090 R-25 H2: this device owns the start. Mark the id so the
  // sweep scopes to workouts the device started, not rows it merely
  // pulled from the server.
  markStartedHere(owner, id);
  fireLocalWrite();
  return out;
}

export interface LogSetInput {
  workoutId: string;
  exerciseId: string;
  set_number: number;
  reps?: number;
  hold_seconds?: number;
}

/** B12: record one set as a log_entries row, tied to its workout_logs row. */
export async function logSet(input: LogSetInput): Promise<string> {
  if (currentOwner == null) {
    throw new Error("db: owner not set — call setOwner(userId) after sign-in");
  }
  const owner = currentOwner;
  const id = newId();
  const db = getDb();
  const out = await db.transaction(
    "rw",
    db.workout_logs,
    db.log_entries,
    db.syncQueue,
    async () => {
      // T-097 §4: flip the held workout_logs create to pending in the
      // SAME transaction as this log_entries create. The push reads
      // status: "pending" and pushes both rows in order; the entry's
      // `workout` foreign key resolves on the server because the
      // workout row is already on the server when the entry lands.
      // The flip must touch the queue row, not the workout_logs row,
      // and must happen BEFORE the entry queue row is added so
      // createdAt ordering puts workout first.
      const held = await db.syncQueue
        .where("[collection+status]")
        .equals(["workout_logs", "held"])
        .filter((e) => e.recordId === input.workoutId)
        .first();
      if (held) {
        await db.syncQueue.update(held.id, { status: "pending" });
      }
      const row: LogEntryRow = {
        id,
        owner,
        updatedAt: nowIso(),
        deletedAt: null,
        workout: input.workoutId,
        exercise: input.exerciseId,
        set_number: input.set_number,
        reps: input.reps,
        hold_seconds: input.hold_seconds,
      };
      await db.log_entries.put(row);
      await db.syncQueue.add(queueEntry("log_entries", id, "create", row));
      return id;
    },
  );
  fireLocalWrite();
  return out;
}

/** B12: mark the workout_logs row as completed when the last set is done. */
export async function finishWorkout(id: string): Promise<void> {
  if (currentOwner == null) {
    throw new Error("db: owner not set — call setOwner(userId) after sign-in");
  }
  const db = getDb();
  await db.transaction("rw", db.workout_logs, db.syncQueue, async () => {
    const existing = await db.workout_logs.get(id);
    if (!existing) throw new Error(`workout ${id} not found`);
    const next: WorkoutLogRow = {
      ...existing,
      id,
      owner: existing.owner,
      updatedAt: nowIso(),
      deletedAt: existing.deletedAt,
      completed_at: nowIso(),
    };
    await db.workout_logs.put(next as WorkoutLogRow);
    await db.syncQueue.add(queueEntry("workout_logs", id, "update", next));
  });
  // T-096 §4: a completed workout can never be swept, so its id has no
  // business in the started-here registry. Without this the registry
  // grew by one id per finished workout for the life of the install.
  clearStartedHere(currentOwner, id);
  fireLocalWrite();
}

/** ADR-0013 D3 + T-090 R-25 H2: tombstone every `workout_logs` row of
 *  `owner` whose `completed_at` is null, that has no log_entries, and
 *  that is older than 60 s. **Scoped to workouts this device
 *  started** — pulled rows are out of scope, otherwise device B can
 *  tombstone device A's mid-write workout and the rev-5 stale guard
 *  then refuses A's finish (the completed workout disappears
 *  everywhere). Called from the auth `user` effect on app open.
 *  Idempotent; runs through the same outbox as any other delete. */
export async function discardEmptyStarts(owner: string): Promise<number> {
  const db = getDb();
  const cutoff = Date.now() - 60_000;
  const startedHere = readStartedHere(owner);
  if (startedHere.size === 0) return 0;
  const rows = (await db.workout_logs
    .where("owner")
    .equals(owner)
    .and((r) => !r.deletedAt && r.completed_at == null)
    .toArray()) as WorkoutLogRow[];
  // T-096 §4: prune registry ids that are not among the candidates. A
  // completed, tombstoned or locally absent row can never be swept, so
  // remembering it only makes the registry grow. Anything left after
  // this loop is a live zero-or-more-set start this device owns.
  const candidateIds = new Set(rows.map((r) => r.id));
  for (const id of Array.from(startedHere)) {
    if (!candidateIds.has(id)) startedHere.delete(id);
  }
  let discarded = 0;
  for (const w of rows) {
    if (!startedHere.has(w.id)) continue; // pulled on another device — never ours
    const started = Date.parse(w.started_at ?? w.updatedAt);
    if (Number.isNaN(started) || started > cutoff) continue;
    const entries = await db.log_entries
      .where("workout")
      .equals(w.id)
      .and((r) => !r.deletedAt)
      .count();
    if (entries > 0) {
      // The workout has entries — it was clearly kept by the user
      // past the start. Remove from the registry so the sweep
      // doesn't look at it again, and skip.
      startedHere.delete(w.id);
      continue;
    }
    await softDeleteWorkout(w.id);
    // The sweep processed this id — remove from the registry so a
    // future sweep doesn't look at the tombstoned row again.
    startedHere.delete(w.id);
    discarded += 1;
  }
  writeStartedHere(owner, startedHere);
  return discarded;
}

/* ------------------------------------------------------------------ */
/* T-090 R-25 H2 — local "started here" registry.                       */
/* ------------------------------------------------------------------ */
/* The sweep is scoped to workouts THIS device started. A pulled row
 * is never in the registry and therefore never swept. The registry
 * is a `Record<owner, Set<id>>` in localStorage; cleared when a
 * row is tombstoned by any path so the sweep doesn't loop on it. */
const STARTED_HERE_KEY = "calicool.startedHere";

function readStartedHere(owner: string): Set<string> {
  try {
    const raw = localStorage.getItem(`${STARTED_HERE_KEY}.${owner}`);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.filter((x): x is string => typeof x === "string"));
  } catch {
    return new Set();
  }
}

function writeStartedHere(owner: string, set: Set<string>): void {
  try {
    localStorage.setItem(
      `${STARTED_HERE_KEY}.${owner}`,
      JSON.stringify(Array.from(set)),
    );
  } catch {
    /* localStorage quota / disabled — the next sweep just
     * re-scans the same row, idempotent. */
  }
}

export function markStartedHere(owner: string, workoutId: string): void {
  const s = readStartedHere(owner);
  s.add(workoutId);
  writeStartedHere(owner, s);
}

function clearStartedHere(owner: string, workoutId: string): void {
  const set = readStartedHere(owner);
  if (set.delete(workoutId)) writeStartedHere(owner, set);
}

/** ADR-0013 D3: last-logged rep count for an exercise, for the runner's
 *  `defaultReps` rule. Looks at non-tombstoned log_entries of the
 *  owner (any workout, complete or not — the user's *recent* reps are
 *  the seed, not the *standard*). */
export async function lastRepsFor(
  owner: string,
  exerciseId: string,
): Promise<number | null> {
  const db = getDb();
  const entries = await db.log_entries
    .where("owner")
    .equals(owner)
    .and((r) => !r.deletedAt && r.exercise === exerciseId)
    .toArray();
  if (entries.length === 0) return null;
  entries.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  return entries[0]!.reps ?? null;
}

// ---------------------------------------------------------------------------
// ADR-0011 — photo capture, compression and the blob lifecycle.
// ---------------------------------------------------------------------------

export interface CapturePhotoInput {
  /** ISO timestamp the photo was taken on the device. */
  taken_at: string;
  weight_kg?: number;
  is_coach_visible?: boolean;
  /** Override the compression pipeline for tests. */
  _compressDeps?: import("./photoCompress").CompressDeps;
}

/** ADR-0011 D1 + D4: capture one progress photo. The whole pipeline is
 *  a single user action from the UI's point of view:
 *    1. compress (D4) — once, on the device, before anything else;
 *    2. refuse if the storage budget is exhausted (D5);
 *    3. write the row, the blob, and the outbox entry in one Dexie
 *       transaction (D1) so partial capture never lands.
 *  The Blob is the source of truth until upload. The row's `photo`
 *  field is `""` until the server hands us the stored filename (D2). */
export async function capturePhoto(
  input: Blob,
  fields: CapturePhotoInput,
): Promise<string> {
  if (currentOwner == null) {
    throw new Error("db: owner not set — call setOwner(userId) after sign-in");
  }
  if (!(input instanceof Blob)) {
    throw new Error("db.capturePhoto: input must be a Blob");
  }
  const { compressPhoto } = await import("./photoCompress");
  const deps =
    fields._compressDeps ??
    (await import("./photoCompress")).browserCompressDeps;
  const compressed = await compressPhoto(input, deps);

  // Budget check BEFORE the transaction so a refused photo never
  // touches Dexie. ADR-0011 D5.
  const { checkPhotoStorageBudget } = await import("./photoStorage");
  const refusal = await checkPhotoStorageBudget(compressed.sizeBytes);
  if (refusal) {
    const err = new Error(refusal) as Error & { code: string };
    err.code = refusal;
    throw err;
  }

  const owner = currentOwner;
  const id = newId();
  const db = getDb();
  const now = nowIso();

  await db.transaction(
    "rw",
    [db.progress_photos, db.photoBlobs, db.syncQueue],
    async () => {
      const row: ProgressPhotoRow = {
        id,
        owner,
        updatedAt: now,
        deletedAt: null,
        taken_at: fields.taken_at,
        photo: "", // ADR-0011 D2 — set after the server stores the file
        weight_kg: fields.weight_kg,
        is_coach_visible: fields.is_coach_visible ?? false,
      };
      await db.progress_photos.put(row);
      await db.photoBlobs.put({
        id,
        blob: compressed.blob,
        mime: compressed.mime,
        sizeBytes: compressed.sizeBytes,
        width: compressed.width,
        height: compressed.height,
        createdAt: now,
        lastUsedAt: now,
        uploaded: false,
      });
      const entry: SyncQueueEntry = {
        id: newQueueId(),
        collection: "progress_photos",
        recordId: id,
        op: "create",
        payload: JSON.stringify(stripInternalFields(row)),
        blobKey: id,
        fileField: "photo",
        attempts: 0,
        status: "pending",
        createdAt: now,
      };
      await db.syncQueue.add(entry);
    },
  );
  fireLocalWrite();
  return id;
}

/** Lightweight pub/sub: the sync manager subscribes and debounces a sync. */
type LocalWriteListener = () => void;
const localWriteListeners: Set<LocalWriteListener> = new Set();

export function onLocalWrite(listener: LocalWriteListener): () => void {
  localWriteListeners.add(listener);
  return () => localWriteListeners.delete(listener);
}

export function fireLocalWrite(): void {
  for (const l of localWriteListeners) l();
}
